import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { PASSWORD_NO_RECENT_REUSE_HINT } from '../utils/validation';

/** Tokens from change_password_light.html */
const CP = {
  paper: '#fffdf7',
  border: '#e0d9c8',
  borderInput: '#d8d0c0',
  text: '#2b2b2b',
  textMuted: '#7a7060',
  label: '#9a8f7e',
  placeholder: '#bdb3a0',
  hint: '#9a8f7e',
  divider: '#e5dfd0',
  iconBg: '#fdf3e3',
  accent: '#c8975a',
  accentHover: '#b8844a',
  focusRing: 'rgba(200, 151, 90, 0.12)',
  inputBg: '#f7f3eb',
  barEmpty: '#e5dfd0',
  strengthColors: ['#E24B4A', '#EF9F27', '#EF9F27', '#3B6D11'] as const,
  errBorder: '#E24B4A',
  errRing: 'rgba(226, 75, 74, 0.08)',
  okBorder: '#3B6D11',
  okRing: 'rgba(59, 109, 17, 0.08)',
  errText: '#A32D2D',
  okText: '#3B6D11',
};

function computeStrength(password: string): { score: number; label: string; tone: 'err' | 'mid' | 'ok' } {
  if (!password.length) return { score: 0, label: '', tone: 'mid' };
  let s = 0;
  if (password.length >= 6) s++;
  if (password.length >= 10) s++;
  if (/[A-Z]/.test(password) && /[0-9]/.test(password)) s++;
  if (/[^A-Za-z0-9]/.test(password)) s++;
  const labels = ['Too short', 'Fair', 'Good', 'Strong'];
  if (s === 0) return { score: 0, label: 'Too short', tone: 'err' };
  const idx = Math.min(4, s) - 1;
  return {
    score: s,
    label: labels[idx],
    tone: s <= 1 ? 'err' : s === 4 ? 'ok' : 'mid',
  };
}

const EyeIcon: React.FC = () => (
  <Box component="svg" viewBox="0 0 16 16" fill="none" sx={{ width: 16, height: 16 }}>
    <path
      d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
      stroke="currentColor"
      strokeWidth={1.2}
    />
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth={1.2} />
  </Box>
);

const HeaderLockIcon: React.FC = () => (
  <Box component="svg" viewBox="0 0 20 20" fill="none" sx={{ width: 20, height: 20 }}>
    <rect x="3.5" y="8.5" width="13" height="10" rx="2.5" stroke={CP.accent} strokeWidth={1.4} />
    <path
      d="M6.5 8.5V6a3.5 3.5 0 017 0v2.5"
      stroke={CP.accent}
      strokeWidth={1.4}
      strokeLinecap="round"
    />
    <circle cx="10" cy="13.5" r="1.5" fill={CP.accent} />
    <line x1="10" y1="15" x2="10" y2="16.5" stroke={CP.accent} strokeWidth={1.4} strokeLinecap="round" />
  </Box>
);

const BtnLockIcon: React.FC = () => (
  <Box component="svg" viewBox="0 0 16 16" fill="none" sx={{ width: 14, height: 14 }}>
    <rect x="3.5" y="7.5" width="9" height="7" rx="2" stroke="#fff" strokeWidth={1.3} />
    <path
      d="M5.5 7.5V5.5a2.5 2.5 0 015 0V7.5"
      stroke="#fff"
      strokeWidth={1.3}
      strokeLinecap="round"
    />
  </Box>
);

export interface ChaaviChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  onCurrentPasswordChange: (v: string) => void;
  onNewPasswordChange: (v: string) => void;
  onConfirmPasswordChange: (v: string) => void;
  submitting: boolean;
  serverError: string | null;
  onSubmit: () => void;
}

const labelSx = {
  fontSize: '0.6875rem',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: CP.label,
  fontFamily: '"Jost", "Roboto", sans-serif',
};

const ChaaviChangePasswordModal: React.FC<ChaaviChangePasswordModalProps> = ({
  open,
  onClose,
  currentPassword,
  newPassword,
  confirmPassword,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  submitting,
  serverError,
  onSubmit,
}) => {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
    }
  }, [open]);

  const strength = useMemo(() => computeStrength(newPassword), [newPassword]);
  const barColor =
    strength.score > 0 ? CP.strengthColors[Math.min(4, strength.score) - 1] : CP.strengthColors[0];

  const matchHint = useMemo(() => {
    if (!confirmPassword.length) return { text: '', tone: 'none' as const };
    if (newPassword === confirmPassword) return { text: 'Passwords match', tone: 'ok' as const };
    return { text: 'Passwords do not match', tone: 'err' as const };
  }, [newPassword, confirmPassword]);

  const confirmInputState =
    confirmPassword.length === 0
      ? 'default'
      : newPassword === confirmPassword
      ? 'success'
      : 'error';

  const baseInputSx = {
    width: '100%',
    boxSizing: 'border-box' as const,
    height: 40,
    border: `0.5px solid ${CP.borderInput}`,
    borderRadius: '8px',
    pr: '40px',
    pl: 1.5,
    fontSize: '0.875rem',
    color: CP.text,
    bgcolor: CP.inputBg,
    fontFamily: '"Jost", "Roboto", "Helvetica Neue", Arial, sans-serif',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
    '&::placeholder': { color: CP.placeholder },
    '&:focus': {
      borderColor: CP.accent,
      boxShadow: `0 0 0 3px ${CP.focusRing}`,
      bgcolor: '#fff',
    },
    '&:disabled': { opacity: 0.55, cursor: 'not-allowed' },
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          width: '100%',
          maxWidth: 440,
          overflow: 'hidden',
          border: `0.5px solid ${CP.border}`,
          bgcolor: CP.paper,
          m: 2,
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(232, 228, 212, 0.88)' },
      }}
    >
      <Box sx={{ pt: 3, px: 3, pb: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '10px',
            bgcolor: CP.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <HeaderLockIcon />
        </Box>
        <Typography
          component="p"
          sx={{
            fontSize: '1.0625rem',
            fontWeight: 500,
            color: CP.text,
            fontFamily: '"Jost", "Roboto", sans-serif',
            m: 0,
            mb: 0.5,
          }}
        >
          Change password
        </Typography>
        <Typography
          component="p"
          sx={{
            fontSize: '0.8125rem',
            color: CP.textMuted,
            fontFamily: '"Jost", "Roboto", sans-serif',
            m: 0,
            lineHeight: 1.45,
          }}
        >
          Enter your current password and choose a new one.
        </Typography>
        <Box sx={{ height: '0.5px', bgcolor: CP.divider, mt: 2.5 }} />
      </Box>

      <Box
        component="form"
        onSubmit={e => {
          e.preventDefault();
          onSubmit();
        }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {serverError ? (
            <Alert severity="error" sx={{ py: 0.5, fontSize: '0.8125rem' }}>
              {serverError}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
            <Typography component="label" htmlFor="cp-current" sx={labelSx}>
              Current password
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Box
                id="cp-current"
                component="input"
                type={showCurrent ? 'text' : 'password'}
                autoComplete="current-password"
                autoFocus
                placeholder="Enter current password"
                disabled={submitting}
                value={currentPassword}
                onChange={e => onCurrentPasswordChange(e.target.value)}
                sx={baseInputSx}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setShowCurrent(v => !v)}
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  p: '2px',
                  color: showCurrent ? CP.accent : CP.placeholder,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#7a6a50' },
                }}
              >
                <EyeIcon />
              </Box>
            </Box>
          </Box>

          <Box sx={{ height: '0.5px', bgcolor: CP.divider, my: 0.25 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
            <Typography component="label" htmlFor="cp-new" sx={labelSx}>
              New password
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: CP.hint, lineHeight: 1.45, mb: 0.25 }}>
              {PASSWORD_NO_RECENT_REUSE_HINT}
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Box
                id="cp-new"
                component="input"
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="At least 6 characters"
                disabled={submitting}
                value={newPassword}
                onChange={e => onNewPasswordChange(e.target.value)}
                sx={baseInputSx}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setShowNew(v => !v)}
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  p: '2px',
                  color: showNew ? CP.accent : CP.placeholder,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#7a6a50' },
                }}
              >
                <EyeIcon />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {[1, 2, 3, 4].map(i => (
                <Box
                  key={i}
                  sx={{
                    height: 3,
                    flex: 1,
                    borderRadius: '2px',
                    bgcolor: strength.score > 0 && i <= strength.score ? barColor : CP.barEmpty,
                    transition: 'background 0.2s',
                  }}
                />
              ))}
            </Box>
            {strength.label ? (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color:
                    strength.tone === 'err' ? CP.errText : strength.tone === 'ok' ? CP.okText : CP.hint,
                  fontFamily: '"Jost", sans-serif',
                }}
              >
                {strength.label}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
            <Typography component="label" htmlFor="cp-confirm" sx={labelSx}>
              Confirm new password
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Box
                id="cp-confirm"
                component="input"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Repeat new password"
                disabled={submitting}
                value={confirmPassword}
                onChange={e => onConfirmPasswordChange(e.target.value)}
                sx={{
                  ...baseInputSx,
                  ...(confirmInputState === 'error'
                    ? {
                        borderColor: CP.errBorder,
                        '&:focus': { borderColor: CP.errBorder, boxShadow: `0 0 0 3px ${CP.errRing}` },
                      }
                    : {}),
                  ...(confirmInputState === 'success'
                    ? {
                        borderColor: CP.okBorder,
                        '&:focus': { borderColor: CP.okBorder, boxShadow: `0 0 0 3px ${CP.okRing}` },
                      }
                    : {}),
                }}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setShowConfirm(v => !v)}
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  p: '2px',
                  color: showConfirm ? CP.accent : CP.placeholder,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#7a6a50' },
                }}
              >
                <EyeIcon />
              </Box>
            </Box>
            {matchHint.text ? (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: matchHint.tone === 'ok' ? CP.okText : CP.errText,
                  fontFamily: '"Jost", sans-serif',
                }}
              >
                {matchHint.text}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            px: 3,
            pb: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            type="button"
            onClick={onClose}
            disabled={submitting}
            sx={{
              height: 36,
              px: 2,
              borderRadius: '8px',
              border: `0.5px solid ${CP.borderInput}`,
              color: CP.textMuted,
              fontSize: '0.875rem',
              textTransform: 'none',
              fontFamily: '"Jost", "Roboto", sans-serif',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#f0ebe0', border: `0.5px solid ${CP.borderInput}` },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            sx={{
              height: 36,
              px: 2.5,
              borderRadius: '8px',
              bgcolor: CP.accent,
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              fontFamily: '"Jost", "Roboto", sans-serif',
              gap: 0.875,
              '&:hover': { bgcolor: CP.accentHover },
              '&:active': { transform: 'scale(0.98)' },
            }}
          >
            {submitting ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              <>
                <BtnLockIcon />
                Update password
              </>
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ChaaviChangePasswordModal;
